import React, { useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import axios from "axios";
import * as C from "../componentsM/index";
import { SITE, whatsappLink } from "../lib/site";
import { errorMessage } from "../lib/api";
import { WhatsAppIcon } from "../componentsM/WhatsAppButton";
import { Alert, Button, TextField } from "../ui";

const BASE = (process.env.REACT_APP_API_ENDPOINT || "").replace(/\/$/, "");
const EMPTY = { fullname: "", email: "", number: "", subject: "", message: "", website: "" };

const ContactDetails = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      const { data } = await axios.post(`${BASE}/api/tismain/contact/`, form);
      setStatus({ tone: "success", text: data.message });
      setForm(EMPTY);
    } catch (err) {
      setStatus({ tone: "error", text: errorMessage(err, "Your message could not be sent. Please try WhatsApp or call us.") });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <C.Hero title="Contact us" tagLine="We'd love to hear from you. Reach us by phone, WhatsApp, email or the form below." />
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-4">
            <a href={`tel:${SITE.uanTel}`} className="flex gap-4 rounded-2xl p-5 ring-1 ring-slate-200 transition hover:ring-navy-300">
              <Phone className="h-6 w-6 shrink-0 text-gold-600" aria-hidden />
              <div>
                <p className="font-display font-bold text-navy-800">Phone</p>
                <p className="text-slate-600">UAN {SITE.uan}</p>
                <p className="text-slate-600">Campus {SITE.tel} (5 lines)</p>
                <p className="text-slate-600">Admissions {SITE.phone}</p>
              </div>
            </a>
            <a
              href={whatsappLink("Assalam o Alaikum, I have a question for The International School.")}
              target="_blank"
              rel="noreferrer"
              className="flex gap-4 rounded-2xl bg-[#25D366]/10 p-5 ring-1 ring-[#25D366]/40 transition hover:ring-[#25D366]"
            >
              <WhatsAppIcon className="h-6 w-6 shrink-0 text-[#128C4B]" />
              <div>
                <p className="font-display font-bold text-navy-800">WhatsApp</p>
                <p className="text-slate-600">{SITE.phone}: the quickest way to reach admissions</p>
              </div>
            </a>
            <a href={`mailto:${SITE.email}`} className="flex gap-4 rounded-2xl p-5 ring-1 ring-slate-200 transition hover:ring-navy-300">
              <Mail className="h-6 w-6 shrink-0 text-gold-600" aria-hidden />
              <div>
                <p className="font-display font-bold text-navy-800">Email</p>
                <p className="text-slate-600">{SITE.email}</p>
              </div>
            </a>
            <div className="flex gap-4 rounded-2xl p-5 ring-1 ring-slate-200">
              <MapPin className="h-6 w-6 shrink-0 text-gold-600" aria-hidden />
              <div className="space-y-3">
                <div>
                  <p className="font-display font-bold text-navy-800">Pakistan Railways Campus</p>
                  <p className="text-slate-600">{SITE.address}</p>
                </div>
                <div>
                  <p className="font-display font-bold text-navy-800">Clifton</p>
                  <p className="text-slate-600">F/23, Block 5, Rojhan Street, Clifton, Karachi 75600</p>
                  <p className="text-slate-600">Tel (+92-21) 35835805-6</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="rounded-3xl bg-slate-50 p-6 ring-1 ring-slate-200 sm:p-8">
            <h2 className="font-display text-2xl font-extrabold text-navy-800">Send us a message</h2>
            <p className="mt-1 text-slate-600">We usually reply within one working day.</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {status && (
                <Alert tone={status.tone} className="sm:col-span-2">
                  {status.text}
                </Alert>
              )}
              <TextField id="fullname" label="Your name" required value={form.fullname} onChange={set("fullname")} />
              <TextField id="number" label="Phone" type="tel" value={form.number} onChange={set("number")} />
              <TextField id="email" label="Email" type="email" className="sm:col-span-2" value={form.email} onChange={set("email")} hint="Give an email or a phone number so we can reply." />
              <TextField id="subject" label="Subject" className="sm:col-span-2" value={form.subject} onChange={set("subject")} />
              <TextField id="message" as="textarea" rows={5} label="Message" className="sm:col-span-2" required value={form.message} onChange={set("message")} />
              <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
              <div className="sm:col-span-2">
                <Button type="submit" size="lg" loading={sending} icon={Send}>
                  Send message
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>
      <C.Map />
    </>
  );
};

export default ContactDetails;
